/**
 * Firma Oluştur — Spec Bölüm 1.5.T5 + S2.T2 devamı
 *
 * Email doğrulandıktan & sign-in olduktan sonra ilk firma oluşturma.
 * Backend: POST /api/manager/organizations
 */
import { useEffect, useState } from 'react';
import { useLocation } from 'wouter';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  CreateOrganizationSchema,
  type CreateOrganizationInput,
} from '@swskills/shared';
import { Header } from '../components/Header.js';
import {
  Alert,
  Button,
  Card,
  Checkbox,
  Field,
  Input,
} from '../components/ui.js';
import { api, ApiError } from '../lib/api.js';
import { useSession } from '../lib/auth-client.js';

const INDUSTRY_OPTIONS: Array<{ value: CreateOrganizationInput['industry']; label: string }> = [
  { value: 'MACHINERY', label: 'Makine İmalat' },
  { value: 'AUTOMOTIVE', label: 'Otomotiv' },
  { value: 'DEFENSE', label: 'Savunma Sanayi' },
  { value: 'CONSUMER', label: 'Tüketim Ürünleri' },
  { value: 'SHEET_FAB', label: 'Sac Metal İmalat' },
  { value: 'PRECISION_MECHANICAL', label: 'Hassas Mekanik' },
  { value: 'OTHER', label: 'Diğer' },
];

export function CreateOrganizationPage(): JSX.Element {
  const [, setLocation] = useLocation();
  const { data: session, isPending } = useSession();
  const [serverError, setServerError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<CreateOrganizationInput>({
    resolver: zodResolver(CreateOrganizationSchema),
    defaultValues: {
      name: '',
      industry: 'MACHINERY',
      teamSize: undefined,
      kvkkAccept: false as unknown as true,
      tosAccept: false as unknown as true,
    },
  });

  // Auth kontrolü — render-side effect değil, useEffect içinde (Better Auth
  // useSession ilk birkaç render'da fresh fetch yapana kadar null dönebilir)
  useEffect(() => {
    if (!isPending && !session?.user) {
      setLocation('/yonetici/giris');
    }
  }, [session, isPending, setLocation]);

  if (isPending) {
    return (
      <div className="min-h-screen bg-slate-50">
        <main className="mx-auto max-w-md px-6 py-12">
          <p className="text-slate-500">Yükleniyor…</p>
        </main>
      </div>
    );
  }

  async function onSubmit(values: CreateOrganizationInput): Promise<void> {
    setServerError(null);
    try {
      await api.post('/api/manager/organizations', {
        ...values,
        teamSize: values.teamSize ? Number(values.teamSize) : undefined,
      });
      // Full reload — session.activeOrgId backend'de güncellendi,
      // useSession cache'i bunu görsün diye fresh fetch gerek.
      window.location.assign('/yonetici/dashboard');
    } catch (err) {
      if (err instanceof ApiError) {
        setServerError(err.message);
      } else {
        setServerError('Beklenmeyen bir hata oluştu');
      }
    }
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <Header />
      <main className="mx-auto max-w-md px-6 py-12">
        <Card>
          <h1 className="mb-2 text-2xl font-bold text-slate-900">
            Firmanızı Oluşturun
          </h1>
          <p className="mb-6 text-sm text-slate-600">
            Adayları davet edip değerlendirme başlatmak için firma bilgilerinizi
            girin. Daha sonra çalışan sayısı vb. detayları güncelleyebilirsiniz.
          </p>

          {serverError && (
            <div className="mb-4">
              <Alert variant="danger">{serverError}</Alert>
            </div>
          )}

          <form onSubmit={handleSubmit(onSubmit)} noValidate>
            <Field
              label="Firma Adı"
              htmlFor="name"
              required
              error={errors.name?.message}
            >
              <Input
                id="name"
                type="text"
                autoComplete="organization"
                error={!!errors.name}
                {...register('name')}
              />
            </Field>

            <Field
              label="Sektör"
              htmlFor="industry"
              required
              error={errors.industry?.message}
              hint="Soru havuzu sektöre göre özelleşir (Spec Bölüm 11)"
            >
              <select
                id="industry"
                className="h-11 w-full rounded-lg border border-slate-300 bg-white px-3 text-base focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                {...register('industry')}
              >
                {INDUSTRY_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </Field>

            <Field
              label="Tahmini Ekip Büyüklüğü"
              htmlFor="teamSize"
              error={errors.teamSize?.message}
              hint="Opsiyonel — sektör raporu için kullanılır"
            >
              <Input
                id="teamSize"
                type="number"
                min={1}
                max={10000}
                error={!!errors.teamSize}
                {...register('teamSize', { valueAsNumber: true })}
              />
            </Field>

            <div className="mt-6 space-y-1">
              <Checkbox
                id="kvkk"
                label={
                  <span>
                    <a
                      href="/kvkk"
                      target="_blank"
                      rel="noreferrer"
                      className="text-blue-900 underline"
                    >
                      KVKK Aydınlatma Metni
                    </a>{' '}
                    ve firma veri sahibi yükümlülüklerini kabul ediyorum.{' '}
                    <span className="text-red-500">*</span>
                  </span>
                }
                error={errors.kvkkAccept?.message}
                {...register('kvkkAccept')}
              />

              <Checkbox
                id="tos"
                label={
                  <span>
                    <a
                      href="/kullanim-sartlari"
                      target="_blank"
                      rel="noreferrer"
                      className="text-blue-900 underline"
                    >
                      Kullanım Şartlarını
                    </a>{' '}
                    okudum ve kabul ediyorum.{' '}
                    <span className="text-red-500">*</span>
                  </span>
                }
                error={errors.tosAccept?.message}
                {...register('tosAccept')}
              />
            </div>

            <Button type="submit" loading={isSubmitting} className="mt-4 w-full">
              Firmayı Oluştur
            </Button>
          </form>
        </Card>
      </main>
    </div>
  );
}
